"use client";

import { useCallback, useState } from "react";

import { useSearchableModalList } from "@/features/user-management/_hooks/useSearchableModalList";

import type { SwitcherSearchState } from "../_types/ui-types";

/** Result shape both server actions return, matching `useSearchableModalList`'s own expectation. */
interface ActionResult<T> {
  success: boolean;
  data?: T[];
}

interface UseSwitcherListOptions<T> {
  /** Whether the switcher popover is open. Forwarded to `useSearchableModalList`. */
  isOpen: boolean;
  /** Lists recent items with offset pagination. */
  listAction: (offset?: number) => Promise<ActionResult<T>>;
  /** Searches items by term. */
  searchAction: (term: string) => Promise<ActionResult<T>>;
  /** Notice shown when the initial or paged list fetch fails. */
  loadErrorMessage: string;
  /** Notice shown when a search fetch fails. */
  searchErrorMessage: string;
}

interface UseSwitcherListReturn<T> {
  /** Browse/search/paging state to hand to `SwitcherSearchPanel`. */
  search: SwitcherSearchState<T>;
  /** The current failure notice, or null when the last fetch succeeded. */
  loadError: string | null;
  /** Clears the notice, for a flow closing its popover. */
  clearLoadError: () => void;
}

/**
 * `useSearchableModalList` with a visible failure notice. That hook ignores a non-success result, so
 * a failed fetch renders as an empty list ("No organizations available" / "No users available"),
 * which reads as a real, empty answer rather than a request that never landed. Both event-ownership
 * flows need exactly that, so the wrapper lives here once instead of being re-declared in each.
 *
 * @param options - See {@link UseSwitcherListOptions}. The two messages differ per flow (the entity
 *   being listed) but the surrounding wiring does not.
 * @returns The list state plus its failure notice.
 */
export function useSwitcherList<T>({
  isOpen,
  listAction,
  searchAction,
  loadErrorMessage,
  searchErrorMessage,
}: UseSwitcherListOptions<T>): UseSwitcherListReturn<T> {
  // States
  const [loadError, setLoadError] = useState<string | null>(null);

  // Hooks
  const search = useSearchableModalList<T>({
    isOpen,
    listAction: async (offset) => {
      const result = await listAction(offset);
      setLoadError(result.success ? null : loadErrorMessage);
      return result;
    },
    searchAction: async (term) => {
      const result = await searchAction(term);
      setLoadError(result.success ? null : searchErrorMessage);
      return result;
    },
  });

  // Handlers
  const clearLoadError = useCallback((): void => setLoadError(null), []);

  return { search, loadError, clearLoadError };
}
